import { useEffect, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { InfiosIconButton } from '@infios/uikit/react';
import styles from './ChatInput.module.css';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  onSend: (text: string) => void;
}

const MAX_ROWS = 5;

/**
 * Auto-growing message composer. Enter sends; Shift+Enter inserts a newline.
 * Disabled (but still readable) while a turn is streaming. Controlled by the
 * parent so a content block can fill it via ComposeMessageContext (see
 * OrderSearchResultsBlock for an example) — replacing whatever text is
 * currently there, same as if the user had typed it.
 */
export function ChatInput({ value, onChange, disabled, onSend }: ChatInputProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Resize whenever the value changes, whether from typing or from an
  // externally composed message.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = 20;
    const maxHeight = lineHeight * MAX_ROWS;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [value]);

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    onChange('');
    requestAnimationFrame(() => {
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const canSend = value.trim().length > 0 && !disabled;

  return (
    <div className={styles.row}>
      <textarea
        ref={textareaRef}
        className={styles.input}
        rows={1}
        value={value}
        placeholder={t('ChatbotUI.InputPlaceholder')}
        aria-label={t('ChatbotUI.InputPlaceholder')}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <InfiosIconButton
        name="send"
        className={styles.sendBtn}
        disabled={!canSend}
        label={t('ChatbotUI.Send')}
        onClick={submit}
      />
    </div>
  );
}
